import { useEffect, useState } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { Moon, Flame, Droplet, Wind, Leaf } from 'lucide-react'
import { findCardById, type TarotCard } from '@yadviga/shared'
import { CardLightbox } from './CardLightbox'

type Size = 'xs' | 'sm' | 'md' | 'lg' | 'hero'

interface Props {
  cardId: string
  reversed?: boolean
  size?: Size
  /** When true, shows the deck back instead of the face. */
  facedown?: boolean
  className?: string
  onClick?: () => void
  /**
   * When true and the card is face-up with no explicit onClick, tapping the
   * card opens a fullscreen lightbox to inspect the artwork. Default true.
   */
  zoomable?: boolean
  /** Optional caption shown in the lightbox (e.g. "Прошлое"). */
  positionLabel?: string
}

const SIZE_CLASSES: Record<Size, string> = {
  xs: 'w-16 h-24',
  sm: 'w-24 h-36',
  md: 'w-32 h-48',
  lg: 'w-44 h-64',
  hero: 'w-56 h-[336px]',
}

export function TarotCardImage({
  cardId,
  reversed = false,
  size = 'md',
  facedown = false,
  className,
  onClick,
  zoomable = true,
  positionLabel,
}: Props) {
  const { i18n } = useTranslation()
  const lang = i18n.language === 'en' ? 'en' : 'ru'
  const card = findCardById(cardId)
  const [imageOk, setImageOk] = useState(!facedown && card !== undefined)
  const [backOk, setBackOk] = useState(facedown)
  const [showLightbox, setShowLightbox] = useState(false)

  // If the dev server wasn't ready when the page first loaded, onError
  // permanently flipped imageOk to false. Reset it whenever the card changes
  // (or whenever the component flips between facedown / face-up) so a hard
  // refresh isn't needed once the asset is reachable.
  useEffect(() => {
    setImageOk(!facedown && card !== undefined)
    setBackOk(facedown)
  }, [cardId, facedown, card])

  if (facedown) {
    return (
      <div
        onClick={onClick}
        className={clsx(
          'relative overflow-hidden rounded-2xl border border-sage-300/60 shadow-md',
          SIZE_CLASSES[size],
          onClick && 'cursor-pointer transition-transform active:scale-95',
          className,
        )}
      >
        {backOk ? (
          <img
            src="/cards/card_back.webp"
            alt=""
            className="h-full w-full object-cover"
            onError={() => setBackOk(false)}
          />
        ) : (
          <FallbackBack />
        )}
      </div>
    )
  }

  if (!card) return null

  const path = `/cards/${card.imageKey}.webp`
  const canZoom = zoomable && !onClick
  const handleClick = onClick ?? (canZoom ? () => setShowLightbox(true) : undefined)

  return (
    <>
      <div
        onClick={handleClick}
        className={clsx(
          'relative overflow-hidden rounded-2xl border border-sage-300/60 bg-cream shadow-md',
          SIZE_CLASSES[size],
          handleClick && 'cursor-pointer transition-transform hover:scale-[1.02] active:scale-95',
          reversed && 'rotate-180',
          className,
        )}
      >
        {imageOk ? (
          <img
            src={path}
            alt={lang === 'en' ? card.nameEn : card.nameRu}
            className="h-full w-full object-cover"
            onError={() => setImageOk(false)}
          />
        ) : (
          <FallbackFace card={card} lang={lang} />
        )}
      </div>
      {showLightbox && (
        <CardLightbox
          card={card}
          reversed={reversed}
          imageUrl={path}
          positionLabel={positionLabel}
          onClose={() => setShowLightbox(false)}
        />
      )}
    </>
  )
}

function FallbackFace({ card, lang }: { card: TarotCard; lang: 'ru' | 'en' }) {
  const Accent =
    card.arcana === 'major'
      ? Moon
      : card.arcana === 'minor' && card.suit === 'wands' ? Flame
      : card.arcana === 'minor' && card.suit === 'cups' ? Droplet
      : card.arcana === 'minor' && card.suit === 'swords' ? Wind
      : Leaf
  return (
    <div className="flex h-full w-full flex-col items-center justify-between bg-gradient-to-b from-cream via-sage-50 to-sage-100 p-2 text-center">
      <span className="text-xs text-sage-600">
        {card.arcana === 'major' ? `Major ${card.number}` : `Minor`}
      </span>
      <div className="flex flex-col items-center gap-1">
        <Accent className="h-8 w-8 text-sage-600" strokeWidth={1.5} aria-hidden />
        <span className="font-serif text-sm leading-tight text-sage-800">
          {lang === 'en' ? card.nameEn : card.nameRu}
        </span>
      </div>
      <span className="text-[10px] text-sage-500 line-clamp-2">
        {(lang === 'en' ? card.keywordsEn : card.keywordsRu).slice(0, 2).join(' · ')}
      </span>
    </div>
  )
}

function FallbackBack() {
  return (
    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#3a4a78] to-[#8b7aa8] text-3xl text-cream">
      ✦
    </div>
  )
}
